import { useTranslation } from "react-i18next";
import { Flex, Box, Spinner } from "@chakra-ui/react";
import time from "src/utils/time";
import useAnimationFrameUpdater from "src/hooks/useAnimationFrameUpdater";
import TextField from "src/components/TextField";
import { H4 } from "src/atoms/typography";
import { Status } from "src/hooks/useAudioRecorder";
import IconOnBackgound from "src/components/IconOnBackground";
import { ReactComponent as MicrophoneIcon } from "src/modules/lullaby/icons/microphone.svg";
import { ReactComponent as PlayIcon } from "src/modules/lullaby/icons/play.svg";
import { ReactComponent as StopIcon } from "src/modules/lullaby/icons/stop.svg";

function RecorderButton({ onClick, icon, ...rest }) {
  return (
    <IconOnBackgound
      bg="white"
      icon={icon}
      fill="primaryBackground"
      iconSize="28px"
      size="56px"
      onClick={onClick}
      {...rest}
    />
  );
}

const mapContentToStatus = {
  [Status.PLAYING]: function Playing({ getCurrentTime, onStopPlaying }) {
    useAnimationFrameUpdater({ updateRate: 2 });
    return (
      <Flex flexDirection="column" alignItems="center">
        <RecorderButton icon={StopIcon} onClick={onStopPlaying} />
        <H4 color="white" mt="16px">
          {time.duration(getCurrentTime()).format("mm:ss")}
        </H4>
      </Flex>
    );
  },
  [Status.LOADING]: function Loading({ duration }) {
    return (
      <Flex flexDirection="column" alignItems="center">
        <Spinner color="white" />
        <H4 color="white" mt="16px">
          {time.duration(duration).format("mm:ss")}
        </H4>
      </Flex>
    );
  },
  [Status.RECORDING]: function Recording({ getRecordingTime, onStopRecording }) {
    useAnimationFrameUpdater({ updateRate: 2 });
    return (
      <Flex flexDirection="column" alignItems="center">
        <RecorderButton icon={StopIcon} onClick={onStopRecording} />
        <H4 color="white" mt="16px">
          {time.duration(getRecordingTime()).format("mm:ss")}
        </H4>
      </Flex>
    );
  },
  [Status.PAUSED_NO_RECORDING]: function PausedNoRecording({ t, onRecord }) {
    return (
      <Flex flexDirection="column" alignItems="center">
        <RecorderButton icon={MicrophoneIcon} onClick={onRecord} />
        <H4 color="white" mt="16px">
          {t("clickToRecord")}
        </H4>
      </Flex>
    );
  },
  [Status.PAUSED_HAS_RECORDING]: function PausedHasRecording({ duration, onRecord, onPlay }) {
    return (
      <Flex flexDirection="column" alignItems="center">
        <Flex>
          <RecorderButton icon={MicrophoneIcon} mr="16px" onClick={onRecord} />
          <RecorderButton icon={PlayIcon} onClick={onPlay} />
        </Flex>
        <H4 color="white" mt="16px">
          {time.duration(duration).format("mm:ss")}
        </H4>
      </Flex>
    );
  }
};

export default function LullabiesEditorView({ audioRecorder, onNameChange, name, duration }) {
  const { t } = useTranslation();
  const RecorderController = mapContentToStatus[audioRecorder.status];

  return (
    <Flex height="100%">
      <Flex
        bg="primaryBackground"
        width="264px"
        mr="48px"
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
        borderRadius="13px"
      >
        <RecorderController
          t={t}
          onRecord={audioRecorder.record}
          onPlay={audioRecorder.play}
          onStopRecording={audioRecorder.stopRecording}
          onStopPlaying={audioRecorder.stopPlaying}
          getCurrentTime={audioRecorder.getCurrentTime}
          getRecordingTime={audioRecorder.getRecordingTime}
          duration={duration}
        />
      </Flex>
      <Box>
        <H4 mb="8px" color="highEmphasis">
          {t("setName")}
        </H4>
        <Box width="196px">
          <TextField
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            type="text"
            placeholder={t("lullaby")}
          />
        </Box>
      </Box>
    </Flex>
  );
}
